import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import type { TenantHealthSnapshot } from '@etiquetachef/types';
import { HealthBadge } from './HealthBadge';

/** Tabela dos tenants problemáticos do snapshot de saúde, com link para `/tenants/{tenantId}`. */
export function HealthList({
  snapshots,
}: {
  snapshots: readonly TenantHealthSnapshot[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const tenantLink = (tenantId: string, label: string): React.JSX.Element => (
    <Link
      href={`/tenants/${tenantId}`}
      onFollow={(e): void => {
        e.preventDefault();
        void navigate(`/tenants/${tenantId}`);
      }}
    >
      {label}
    </Link>
  );

  return (
    <Table<TenantHealthSnapshot>
      items={[...snapshots]}
      trackBy="tenantId"
      variant="container"
      header={<Header variant="h3">{t('health.list.title', { count: snapshots.length })}</Header>}
      empty={<Box textAlign="center" color="inherit">{t('health.empty')}</Box>}
      columnDefinitions={[
        {
          id: 'tenant',
          header: t('health.list.tenant'),
          cell: (s): React.ReactNode => tenantLink(s.tenantId, s.tenantId),
        },
        {
          id: 'primary',
          header: t('health.list.primarySignal'),
          cell: (s): React.ReactNode => <HealthBadge kind={s.primarySignal} />,
        },
        {
          id: 'all',
          header: t('health.list.allSignals'),
          cell: (s): React.ReactNode => (
            <SpaceBetween direction="horizontal" size="xxs">
              {s.signals
                .filter((k) => k !== s.primarySignal)
                .map((k) => (
                  <HealthBadge key={k} kind={k} />
                ))}
            </SpaceBetween>
          ),
        },
        {
          id: 'actions',
          header: t('health.list.actions'),
          cell: (s): React.ReactNode => (
            <Button
              onClick={(): void => {
                void navigate(`/tenants/${s.tenantId}`);
              }}
            >
              {t('health.list.investigate')}
            </Button>
          ),
        },
      ]}
    />
  );
}
